class RelatedProducts {
  constructor(section) {
    this.section = section;

    this.sectionId = section.dataset.sectionId;
    this.productId = section.dataset.productId;
    this.limit = section.dataset.limit || '4';
    this.intent = section.dataset.intent || 'related';
    this.baseUrl = section.dataset.url;

    this.abortController = null;

    this.load();
  }

  getRequestUrl() {
    if (
      !this.baseUrl ||
      !this.sectionId ||
      !this.productId
    ) {
      return null;
    }

    const url = new URL(
      this.baseUrl,
      window.location.origin
    );

    url.searchParams.set(
      'section_id',
      this.sectionId
    );

    url.searchParams.set(
      'product_id',
      this.productId
    );

    url.searchParams.set(
      'limit',
      this.limit
    );

    url.searchParams.set(
      'intent',
      this.intent
    );

    return url.toString();
  }

  async load() {
    const requestUrl = this.getRequestUrl();

    if (!requestUrl) {
      this.section.remove();
      return;
    }

    this.abortController?.abort();
    this.abortController = new AbortController();

    try {
      const response = await fetch(requestUrl, {
        signal: this.abortController.signal,
        headers: {
          Accept: 'text/html'
        }
      });

      if (!response.ok) {
        throw new Error(
          `Related products request failed: ${response.status}`
        );
      }

      const responseText = await response.text();

      const responseDocument =
        new DOMParser().parseFromString(
          responseText,
          'text/html'
        );

      const replacement = responseDocument.querySelector(
        `[data-related-products][data-section-id="${CSS.escape(
          this.sectionId
        )}"]`
      );

      const hasProducts = Boolean(
        replacement?.querySelector(
          '[data-related-products-grid] .related-products__item'
        )
      );

      if (!replacement || !hasProducts) {
        this.section.remove();
        return;
      }

      this.section.replaceWith(replacement);

      initialiseRelatedProductsCarousel(replacement);
    } catch (error) {
      if (error.name === 'AbortError') return;

      console.error(
        'Unable to load related products.',
        error
      );

      this.section.remove();
    }
  }
}

function initialiseRelatedProductsCarousel(section) {
  const grid = section.querySelector(
    '[data-related-products-grid]'
  );

  const previousButton = section.querySelector(
    '[data-related-products-previous]'
  );

  const nextButton = section.querySelector(
    '[data-related-products-next]'
  );

  const currentElement = section.querySelector(
    '[data-related-products-current]'
  );

  const items = Array.from(
    section.querySelectorAll(
      '.related-products__item'
    )
  );

  if (!grid || items.length < 2) return;

  let scrollTimer = null;

  function isMobileCarousel() {
    return window.matchMedia(
      '(max-width: 749px)'
    ).matches;
  }

  function getItemGap() {
    const styles = window.getComputedStyle(grid);

    return Number.parseFloat(styles.columnGap) || 0;
  }

  function getScrollAmount() {
    const firstItem = items[0];

    if (!firstItem) {
      return grid.clientWidth;
    }

    return firstItem.getBoundingClientRect().width +
      getItemGap();
  }

  function getCurrentIndex() {
    const scrollAmount = getScrollAmount();

    if (!scrollAmount) return 0;

    return Math.max(
      0,
      Math.min(
        items.length - 1,
        Math.round(grid.scrollLeft / scrollAmount)
      )
    );
  }

  function updateControls() {
    if (!isMobileCarousel()) {
      previousButton?.removeAttribute('disabled');
      nextButton?.removeAttribute('disabled');

      return;
    }

    const currentIndex = getCurrentIndex();

    if (currentElement) {
      currentElement.textContent =
        String(currentIndex + 1);
    }

    if (previousButton) {
      previousButton.disabled =
        currentIndex <= 0;
    }

    if (nextButton) {
      nextButton.disabled =
        currentIndex >= items.length - 1;
    }
  }

  function scrollByItem(direction) {
    grid.scrollBy({
      left: getScrollAmount() * direction,
      behavior: window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches
        ? 'auto'
        : 'smooth'
    });
  }

  previousButton?.addEventListener(
    'click',
    function () {
      scrollByItem(-1);
    }
  );

  nextButton?.addEventListener(
    'click',
    function () {
      scrollByItem(1);
    }
  );

  grid.addEventListener(
    'scroll',
    function () {
      window.clearTimeout(scrollTimer);

      scrollTimer = window.setTimeout(
        updateControls,
        60
      );
    },
    {
      passive: true
    }
  );

  window.addEventListener(
    'resize',
    updateControls
  );

  updateControls();
}

function initialiseRelatedProducts(root = document) {
  root
    .querySelectorAll('[data-related-products]')
    .forEach(function (section) {
      /*
       * A grid means Shopify has already returned the
       * recommendation results. Only initialise controls.
       */
      if (
        section.querySelector(
          '[data-related-products-grid]'
        )
      ) {
        initialiseRelatedProductsCarousel(section);
        return;
      }

      new RelatedProducts(section);
    });
}

document.addEventListener(
  'DOMContentLoaded',
  function () {
    initialiseRelatedProducts();
  }
);

document.addEventListener(
  'shopify:section:load',
  function (event) {
    initialiseRelatedProducts(event.target);
  }
);